import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Paper,
  Stack,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  DeleteForever,
  People,
  LocationOn,
  History,
  Schedule,
  Warning,
  AdminPanelSettings,
  Group,
  Phone,
  Edit
} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import { useUserRole } from '../contexts/UserRoleContext';
import OSPanelPinAccess from '../components/OSPanelPinAccess';

interface User {
  id: string;
  username: string;
  email: string;
  hp_number?: string;
  user_role: string;
  user_group?: string;
  created_at: string;
}

interface DeleteStats {
  shared_locations: number;
  location_history: number;
  event_schedules: number;
  emergency_alerts: number;
}

export default function OSPanel() {
  const { userRole, loading: roleLoading } = useUserRole();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<string>('');
  const [deleteStats, setDeleteStats] = useState<DeleteStats>({
    shared_locations: 0,
    location_history: 0,
    event_schedules: 0,
    emergency_alerts: 0
  });
  const [deleting, setDeleting] = useState(false);
  const [pinAccessOpen, setPinAccessOpen] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(12);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleUser, setRoleUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState('user');
  const [updatingRole, setUpdatingRole] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
      fetchDeleteStats();
    }
  }, [isAuthenticated]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeleteStats = async () => {
    try {
      const stats = await Promise.all([
        supabase.from('shared_location').select('id', { count: 'exact' }),
        supabase.from('location_history').select('id', { count: 'exact' }),
        supabase.from('event_schedules').select('id', { count: 'exact' }),
        supabase.from('emergency_alerts').select('id', { count: 'exact' })
      ]);

      setDeleteStats({
        shared_locations: stats[0].count || 0,
        location_history: stats[1].count || 0,
        event_schedules: stats[2].count || 0,
        emergency_alerts: stats[3].count || 0
      });
    } catch (err) {
      console.error('Error fetching delete stats:', err);
    }
  };

  const handleDeleteData = async () => {
    if (!deleteType) return;

    try {
      setDeleting(true);
      setError(null);

      let result;
      switch (deleteType) {
        case 'shared_locations':
          result = await supabase.from('shared_location').delete().neq('id', '00000000-0000-0000-0000-000000000000');
          break;
        case 'location_history':
          result = await supabase.from('location_history').delete().neq('id', '00000000-0000-0000-0000-000000000000');
          break;
        case 'event_schedules':
          result = await supabase.from('event_schedules').delete().neq('id', '00000000-0000-0000-0000-000000000000');
          break;
        case 'emergency_alerts':
          result = await supabase.from('emergency_alerts').delete().neq('id', '00000000-0000-0000-0000-000000000000');
          break;
        default:
          throw new Error('Invalid delete type');
      }

      if (result.error) throw result.error;

      setSuccess(`Successfully deleted all ${deleteType.replace('_', ' ')} data`);
      setDeleteDialogOpen(false);
      setDeleteType('');
      fetchDeleteStats();
    } catch (err) {
      setError(`Failed to delete ${deleteType.replace('_', ' ')} data`);
      console.error('Error deleting data:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (type: string) => {
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };



  const getRoleColor = (role: string) => {
    switch (role) {
      case 'god': return 'error';
      case 'admin': return 'warning';
      default: return 'default';
    }
  };

  const getGroupColor = (group?: string) => {
    if (!group) return 'default';
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'error'];
    const index = group.length % colors.length;
    return colors[index] as any;
  };

  const getDeleteButtonConfig = (type: string) => {
    const configs = {
      shared_locations: { icon: <LocationOn />, label: 'Shared Locations', count: deleteStats.shared_locations },
      location_history: { icon: <History />, label: 'Location History', count: deleteStats.location_history },
      event_schedules: { icon: <Schedule />, label: 'Event Schedules', count: deleteStats.event_schedules },
      emergency_alerts: { icon: <Warning />, label: 'Emergency Alerts', count: deleteStats.emergency_alerts }
    };
    return configs[type as keyof typeof configs];
  };

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / usersPerPage);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const openRoleDialog = (user: User) => {
    setRoleUser(user);
    setNewRole(user.user_role);
    setRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!roleUser) return;
    setUpdatingRole(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('users')
        .update({ user_role: newRole })
        .eq('id', roleUser.id);
      if (error) throw error;
      setSuccess(`Updated role for ${roleUser.username} to ${newRole}`);
      setRoleDialogOpen(false);
      setRoleUser(null);
      fetchUsers();
    } catch (err) {
      setError('Failed to update user role');
      console.error('Error updating user role:', err);
    } finally {
      setUpdatingRole(false);
    }
  };

  // Check if user has god role - redirect if not
  if (!roleLoading && userRole?.user_role !== 'god') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          Access denied. Only users with 'god' role can access the OS Panel.
        </Alert>
      </Container>
    );
  }

  // Show PIN access dialog if not authenticated
  if (!isAuthenticated) {
    return (
      <OSPanelPinAccess
        open={pinAccessOpen}
        onClose={() => window.history.back()}
        onSuccess={() => {
          setIsAuthenticated(true);
          setPinAccessOpen(false);
        }}
      />
    );
  }

  if (loading || roleLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <AdminPanelSettings sx={{ mr: 2, fontSize: 40, color: '#c62828' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#c62828' }}>
            OS Panel
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Data Management Section */}
        <Card sx={{ mb: 4, border: '2px solid #f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#c62828' }}>
              Data Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Warning: These actions will permanently delete all data from the specified tables. This action cannot be undone.
            </Typography>
            
            <Grid container spacing={2}>
              {['shared_locations', 'location_history', 'event_schedules', 'emergency_alerts'].map((type) => {
                const config = getDeleteButtonConfig(type);
                return (
                  <Grid item xs={12} sm={6} md={4} key={type}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Box sx={{ mb: 1 }}>
                          {config.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom>
                          {config.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {config.count} records
                        </Typography>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteForever />}
                          onClick={() => openDeleteDialog(type)}
                          fullWidth
                          disabled={config.count === 0}
                        >
                          Delete All
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>

        {/* Users Section */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box display="flex" alignItems="center">
                <People sx={{ mr: 2, fontSize: 30, color: '#c62828' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#c62828' }}>
                  All Users ({users.length})
                </Typography>
              </Box>
              {totalPages > 1 && (
                <Typography variant="body2" color="text.secondary">
                  Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, users.length)} of {users.length} users
                </Typography>
              )}
            </Box>
            
            <Grid container spacing={3}>
              {currentUsers.map((user) => (
                <Grid item xs={12} sm={6} md={4} key={user.id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ mr: 2, bgcolor: '#c62828' }}>
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          icon={<AdminPanelSettings />}
                          label={user.user_role}
                          color={getRoleColor(user.user_role)}
                          size="small"
                        />
                        {user.user_group && (
                          <Chip
                            icon={<Group />}
                            label={user.user_group}
                            color={getGroupColor(user.user_group)}
                            size="small"
                          />
                        )}
                      </Stack>
                      
                      {user.hp_number && (
                        <Box display="flex" alignItems="center" mb={1}>
                          <Phone sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {user.hp_number}
                          </Typography>
                        </Box>
                      )}
                      
                                             <Typography variant="caption" color="text.secondary">
                         Joined: {new Date(user.created_at).toLocaleDateString()}
                       </Typography>
                       
                       <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                         <Button
                           variant="outlined"
                           color="primary"
                           size="small"
                           startIcon={<Edit />}
                           onClick={() => openRoleDialog(user)}
                           sx={{
                             borderColor: '#1976d2',
                             color: '#1976d2',
                             '&:hover': {
                               borderColor: '#115293',
                               backgroundColor: 'rgba(25, 118, 210, 0.04)'
                             }
                           }}
                         >
                           Update Role
                         </Button>
                       </Box>
                     </CardContent>
                   </Card>
                 </Grid>
               ))}
            </Grid>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#c62828', fontWeight: 'bold' }}>
          Confirm Data Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete all {deleteType.replace('_', ' ')} data?
          </Typography>
          <Alert severity="warning">
            This action will permanently delete all data from the {deleteType.replace('_', ' ')} table. 
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteData}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteForever />}
          >
            {deleting ? 'Deleting...' : 'Delete All Data'}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Update Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          Update User Role
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Update role for <strong>{roleUser?.username}</strong>:
          </Typography>
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Role</InputLabel>
                             <Select
                 labelId="role-select-label"
                 value={newRole}
                 label="Role"
                 onChange={e => setNewRole(e.target.value)}
               >
                 <MenuItem value="god">god</MenuItem>
                 <MenuItem value="admin">admin</MenuItem>
                 <MenuItem value="gic">gic</MenuItem>
                 <MenuItem value="user">user</MenuItem>
               </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)} disabled={updatingRole}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            color="primary"
            variant="contained"
            disabled={updatingRole}
            startIcon={updatingRole ? <CircularProgress size={20} /> : <Edit />}
          >
            {updatingRole ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 